import { useCallback, useContext } from 'react'
import CommandCenterService from '../../../service/CommandCenterService';
import { getApiFailureMessage, showToastMessage } from '../../../utils/commonFunctions';
import { getSecurityConfiguration } from '../../CommonComponents/security-configuration/helperFunctions';
import { EncripTionOptionType, SecurityConfigFormData } from '../../CommonComponents/security-configuration/types';
import { CommandCenterStateProviderContext } from './../context';

const useSecurityConfig = () => {
    const context = useContext(CommandCenterStateProviderContext)
    if (!context) {
        throw new Error("useSecurityConfig must be used within a CommandCenterStateProvider")
    }

    const { securityConfig, setSecurityConfig, securityConfigFormData, setSecurityConfigFormData, selected_satellite, EncripTionOptions } = context

    const resetSecurityConfigFormData = () => {
        setSecurityConfigFormData({
            taskType: null,
            keyType: null,
            keyValue: '',
            keyExchangeType: 'key',
            activationFlag: null,
            loading: false,
            encriptionEnabled: true,
            encriptionKey: EncripTionOptions[0],
        })
    }
    const validateSecurityConfigFormData = () => {
        if (!securityConfigFormData) return true;
        const { taskType, keyType, keyValue, keyExchangeType, loading } = securityConfigFormData
        if (!taskType || !keyType || loading || (taskType.value.includes('upload') && !keyValue) || !keyExchangeType) return true;
        if (taskType.value.includes('upload') && /[^a-zA-Z0-9]/.test(keyValue)) return true;
        return false;
    }

    const getResponseMessage = (response: any, taskType: string) => {
        const message = response?.['message'] ?? null
        if (message) return message
        if (taskType === 'upload_activate_secret_key') {
            return 'Secret key uploaded and activated successfully'
        }
        if (taskType === 'upload_deactivate_secret_key') {
            return 'Secret key uploaded and deactivated successfully'
        }
        if (taskType === 'activate_secret_key') {
            return 'Secret key activated successfully'
        }
        if (taskType === 'deactivate_secret_key') {
            return 'Secret key deactivated successfully'
        }
        return 'Task executed successfully'
    }

    const handleUpdateSecurityConfigFormData = async (contactId: string, scenarioId: string, truetwinId: string, executeNow: boolean, data: SecurityConfigFormData) => {
        if (!selected_satellite) return
        const { taskType, keyType, keyValue, keyExchangeType, activationFlag } = data
        const isUpload = taskType?.value.includes('upload')
        const newKeyType = keyType?.value.includes('authentication') ? 'authentication' : 'encryption'
        const newKeySlotId = Number(keyType?.value?.split('_')[1] || '1') - 1
        const newKeyExchangeType = keyExchangeType === 'seed' ? 0 : 1
        const satelliteId = selected_satellite?.value?.satelliteId
        const encriptioEnabled = data?.encriptionEnabled && data?.encriptionKey?.value !== 'none'
        const encriptionKey = data?.encriptionKey?.value !== 'none' ? data?.encriptionKey?.id : 0

        const payload = isUpload ? {
            "keyType": newKeyType,
            "keySlotId": newKeySlotId,
            "keyValue": keyValue,
            "keyExchangeType": newKeyExchangeType,
            "activationFlag": activationFlag ? 1 : 0,
            "IsEncryptionEnabled": encriptioEnabled,
            "ExtendedHeaderIdx": encriptionKey

        } : {
            "keyType": newKeyType,
            "keySlotId": newKeySlotId,
            "activationFlag": activationFlag ? 1 : 0,
            "IsEncryptionEnabled": encriptioEnabled,
            "ExtendedHeaderIdx": encriptionKey
        }

        try {
            const response = isUpload ?
                await CommandCenterService.uploadSecurityConfig(contactId, scenarioId, truetwinId, executeNow, payload) :
                await CommandCenterService.activateSecurityConfig(contactId, scenarioId, truetwinId, executeNow, payload)
            const data = await getSecurityConfiguration(satelliteId)
            setSecurityConfig((prev) => data ? data : prev)
            const message = getResponseMessage(response, taskType?.value!)
            showToastMessage('success', message)
        } catch (error) {
            const message = getApiFailureMessage(error)
            showToastMessage('error', message)
        } finally {
            setSecurityConfigFormData((prev) => prev ? { ...prev, loading: false } : prev);
        }
    }

    const getSortedSecurityIndex = (options: EncripTionOptionType[]) => {
        return options.map((item, i, arr) => ({
            ...item,
            prevIndex: arr.length > 1 ? (i - 1 + arr.length) % arr.length : 0,
            nextIndex: arr.length > 1 ? (i + 1) % arr.length : 0,
        }));
    }

    const getRelatedSecurityIndex = (indexOptions: EncripTionOptionType[], keyType: null | { label: string, value: string }) => {
        if (!keyType) return indexOptions
        const selectedKey = keyType?.value
        switch (selectedKey) {
            case 'authentication_1':
                const firstAuthOptions = indexOptions.filter((option) => [1, 2].includes(option.id))
                return getSortedSecurityIndex(firstAuthOptions)
            case 'authentication_2':
                const secondAuthOptions = indexOptions.filter((option) => [0].includes(option.id))
                return getSortedSecurityIndex(secondAuthOptions)
            case 'encryption_1':
                const firstEncryptionOptions = indexOptions.filter((option) => [1, 2].includes(option.id))
                return getSortedSecurityIndex(firstEncryptionOptions)
            case 'encryption_2':
                const secondEncryptionOptions = indexOptions.filter((option) => [0, 2].includes(option.id))
                return getSortedSecurityIndex(secondEncryptionOptions)
            case 'encryption_3':
                const thirdEncryptionOptions = indexOptions.filter((option) => [0, 1].includes(option.id))
                return getSortedSecurityIndex(thirdEncryptionOptions)
            default:
                return indexOptions
        }
    }

    return {
        securityConfig,
        EncripTionOptions,
        setSecurityConfig,
        securityConfigFormData,
        getRelatedSecurityIndex,
        setSecurityConfigFormData,
        resetSecurityConfigFormData,
        validateSecurityConfigFormData,
        handleUpdateSecurityConfigFormData
    }
}

export default useSecurityConfig